import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";

import { fetchTopicsStoriesFromApi } from "~api/fetchTopicsStoriesFromApi";
import { NUMBER_OF_STORIES_PER_TOPIC } from "~components/TopicModule/constants";
import { getTopics } from "~components/TopicModule/utils";
import { DaperStoryWithImage, Topic } from "~components/TopicModule/types";

type UseTopicsStoriesProps = {
  bconnectId: string;
  storyIdsToExclude: string;
  storyLimit?: number;
};

export const useTopicsStories = ({
  bconnectId,
  storyIdsToExclude,
  storyLimit = NUMBER_OF_STORIES_PER_TOPIC,
}: UseTopicsStoriesProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState();
  const [topicsStoriesError, setTopicsStoriesError] = useState<unknown>();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicsStories, setTopicsStories] = useState<
    Record<string, DaperStoryWithImage[]>
  >({});

  useEffect(() => {
    getTopics({ bconnectId })
      .then((data) => setTopics(data))
      .catch((e) => setTopicsError(e));
  }, [bconnectId]);

  const topicIds = useMemo(() => topics.map((topic) => topic.id), [topics]);

  useEffect(() => {
    if (!topics.length) return undefined;

    const controller = new AbortController();

    const fetchTopicsStories = async () => {
      try {
        const newTopicsStories = await fetchTopicsStoriesFromApi(
          {
            topicIds,
            storyLimit,
            storyIdsToExclude,
            dedupeEnabled: true,
          },
          controller.signal
        );

        setIsLoading(false);
        setTopicsStories(newTopicsStories);
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === "AbortError") {
          console.log("Fetch aborted - safe to ignore");

          return;
        }
        setTopicsStoriesError(e);
      }
    };

    fetchTopicsStories();

    return () => {
      controller.abort();
    };
  }, [storyIdsToExclude, topicIds]);

  return {
    topicsError,
    topicsStoriesError,
    isLoading,
    setTopics,
    topics,
    topicsStories,
  };
};
